/**
 * Estate (property) listing and detail API. Backed by:
 *
 *   GET  /api/v1/smt/estates           — list with filter/pagination
 *   GET  /api/v1/smt/estates/:id       — single estate
 *
 * Level-based redaction is enforced by the backend, not by this module:
 * level=0 receives the AnonEstate shape, level≥1 the full Estate. The caller
 * decides at runtime which type to expect.
 */
import { request, requestData } from './client.ts';
import type { AnonEstate, Estate, EstateFilter, PaginatedResponse } from '../types/api.ts';

/**
 * List estates. The backend response is a PaginatedResponse where `data` is
 * either Estate[] (level≥1) or a redacted projection (level=0). We type as
 * the broader union so consumers can narrow.
 */
export async function listEstates(
  filter: EstateFilter = {},
): Promise<PaginatedResponse<Estate | AnonEstate>> {
  // The List handler reads pagination/filters from query params (form-binding
  // in Gin), not body. The shared `request` helper handles JSON-only payloads
  // so we splice the filter into `query` instead.
  return request<PaginatedResponse<Estate | AnonEstate>>('/smt/estates', {
    query: filter as Record<string, string | number | undefined>,
  });
}

export async function getEstate(id: number): Promise<Estate> {
  return requestData<Estate>(`/smt/estates/${id}`);
}

/**
 * Fetch an Ollama-translated English version of the estate's Finnish AI
 * analysis. Backed by `GET /api/v1/smt/estates/:id/ai-translation?lang=en`.
 *
 * The backend caches results in-memory keyed by the source-text hash, so
 * repeated calls for the same estate are cheap. We additionally cache in
 * `sessionStorage` to avoid even the round-trip during a single browsing
 * session.
 *
 * Returns the translated text, or `null` when no AI text exists / the
 * translator is unavailable. Callers should fall back to the original FI
 * string in that case.
 */
export async function getAITranslation(id: number, lang: 'en'): Promise<string | null> {
  const cacheKey = `smt_ai_xl:${id}:${lang}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch {
    /* sessionStorage may be unavailable (private mode) — ignore. */
  }
  try {
    const data = await requestData<{ lang: string; text: string; cached: boolean }>(
      `/smt/estates/${id}/ai-translation?lang=${encodeURIComponent(lang)}`,
    );
    if (data && typeof data.text === 'string' && data.text.trim().length > 0) {
      try {
        sessionStorage.setItem(cacheKey, data.text);
      } catch {
        /* ignore quota errors */
      }
      return data.text;
    }
    return null;
  } catch {
    // 403/404/503 are normal — anonymous users, properties without AI
    // text, and missing OLLAMA_API_KEY all surface here. We swallow and
    // let the caller display the FI original.
    return null;
  }
}
